/**
 * Widget layout and grid system constants
 * Shared across different widget types for consistent sizing and layout behavior
 */

export class WIDGET_DIMENSIONS {
    /**
     * Base height for widgets in pixels
     * Used as the foundation for calculating widget heights in the grid system
     * - Simple widgets use this as minimum height and scale based on channel count
     * - Lobby view widgets use this as fixed height
     * - Other widgets can use this for consistent baseline sizing
     */
    static BASE_HEIGHT = 128;
    
    /**
     * Height calculation multipliers for dynamic widgets (like simple widgets)
     * Formula: height = BASE_HEIGHT * (HEIGHT_BASE_MULTIPLIER + itemCount * HEIGHT_ITEM_MULTIPLIER)
     * Examples:
     * - 1 item: 128 * (0.5 + 1 * 0.5) = 128px
     * - 2 items: 128 * (0.5 + 2 * 0.5) = 192px
     * - 3 items: 128 * (0.5 + 3 * 0.5) = 256px
     */
    static HEIGHT_BASE_MULTIPLIER = 0.5;
    static HEIGHT_ITEM_MULTIPLIER = 0.5;
    
    /**
     * Calculate height for simple widgets based on item count
     * @param itemCount Number of items (channels, rows, etc.)
     * @returns Calculated height in pixels
     */
    static getSimpleWidgetHeight(itemCount: number): number {
        const heightMultiplier = this.HEIGHT_BASE_MULTIPLIER + (itemCount * this.HEIGHT_ITEM_MULTIPLIER);
        return Math.round(this.BASE_HEIGHT * heightMultiplier);
    }
    
    /**
     * Calculate height for chart widgets based on size
     * SIZE_1 (tiny): Same as 2-channel simple widget (192px)
     * SIZE_2 (small): Same as 3-channel simple widget (256px)
     * SIZE_3 (medium): Same as 4-channel simple widget (320px)
     * SIZE_4 (large): Same as 4-channel simple widget 
     * SIZE_6 (full): No change (keeps original behavior)
     * @param chartSize Chart size constant ('1', '2', '3', '4', '6')
     * @returns Calculated height in pixels, or null if no height should be applied
     */
    static getChartWidgetHeight(chartSize: string): number | null{
        switch (chartSize) {
            case '1': // SIZE_1 - tiny (2-channel widget height)
                return this.getSimpleWidgetHeight(2);
            case '2': // SIZE_2 - small (3-channel widget height)
                return this.getSimpleWidgetHeight(3);
            case '3': // SIZE_3 - medium (4-channel widget height)
                return this.getSimpleWidgetHeight(4);
            case '4': // SIZE_4 - large
                return this.getSimpleWidgetHeight(5);
            case '6': // SIZE_6 - full
            default:
                return null; // No height change for full size
        }
    }
}


// WEBPACK FOOTER //
// ./srv/public/js/common/widgetLayout.ts